import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import PageSeo from './PageSeo';

const FALLBACK_LOGO = 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png';

const parseMarkdown = (text) => {
  if (!text) return text;
  return String(text).split('\n').map((line, li) => (
    <React.Fragment key={li}>
      {line.split(/(\*\*.*?\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i} className="font-semibold text-[#2A312C]">{part.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
      <br />
    </React.Fragment>
  ));
};

/**
 * Coquille publique réutilisable pour les pages éditoriales/SEO.
 * - SEO par page (title + meta description) via PageSeo
 * - H1, intro et sections (H2 + texte) administrables (source: /api/pages/{pageId})
 * - children = widget dynamique éventuel (tarifs, menus, zones...) rendu sous l'intro
 * - relatedLinks = maillage interne crawlable
 */
export default function ContentPage({ pageId, children, relatedLinks = [], ctaLabel = 'Réserver ma prestation' }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    publicApi.getPageContent(pageId)
      .then((res) => { if (active) setPage(res.data); })
      .catch(() => { if (active) setPage(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [pageId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  const p = page || {};
  const sections = (p.sections || []).filter((s) => s.is_active !== false);

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid={`content-page-${pageId}`}>
      <PageSeo title={p.seo_title || p.title} description={p.meta_description} />

      {/* Header */}
      <header className="bg-white border-b border-[#E3DED5]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="content-logo-link">
            <img src={FALLBACK_LOGO} alt="Mitonner" className="h-9 w-auto" />
          </Link>
          <Link to="/" className="btn-primary text-sm" data-testid="content-cta-top">{ctaLabel}</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C] mb-6 text-sm" data-testid="content-back-link">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>

        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-semibold text-[#2A312C] mb-4" data-testid="content-h1">
          {p.h1 || p.title}
        </h1>

        {p.intro && (
          <p className="text-base text-[#5C6A61] leading-relaxed mb-8" data-testid="content-intro">
            {parseMarkdown(p.intro)}
          </p>
        )}

        {/* Widget dynamique éventuel */}
        {children}

        {/* Sections éditoriales (H2 + texte) */}
        {sections.length > 0 && (
          <div className="space-y-6 mt-8">
            {sections.map((section) => (
              <section key={section.id} className="bg-white rounded-2xl border border-[#E3DED5] p-6" data-testid={`content-section-${section.id}`}>
                {section.title && (
                  <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#2A312C] mb-2">{section.title}</h2>
                )}
                {section.content && (
                  <div className="text-[#5C6A61] leading-relaxed">{parseMarkdown(section.content)}</div>
                )}
              </section>
            ))}
          </div>
        )}

        {/* Maillage interne */}
        {relatedLinks.length > 0 && (
          <nav className="mt-10 pt-6 border-t border-[#E3DED5]" data-testid="content-related-links" aria-label="Pages liées">
            <h2 className="font-semibold text-[#2A312C] mb-3">À découvrir aussi</h2>
            <ul className="space-y-2">
              {relatedLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[#C86B51] font-medium hover:underline">{l.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="mt-10">
          <Link to="/" className="btn-primary" data-testid="content-cta-bottom">{ctaLabel}</Link>
        </div>
      </main>
    </div>
  );
}
